import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { PersistConfig, persistReducer } from 'redux-persist';

import { LessonTaskStepKeys } from '../../../main/constants/lesson-task-step-keys.ts';
import { LessonTaskStepKey } from '../../../main/types/asset/lesson.types.ts';
import { getLessonsUtil } from '../../../main/utils/asset/lessons.util.ts';
import { getReduxStorageUtil } from '../../../main/utils/storage/redux-storage.util.ts';

import { LESSON_SLICE_NAME, LessonSliceState } from './lesson.slice.types.ts';
import { openLessonThunkExtraReducers } from './thunks/open-lesson.thunk.ts';
import { updateLessonTaskStepFromKey } from './updaters/lesson-task-step.updater.ts';

const lessonUtil = getLessonsUtil();

export const LESSON_SLICE_INITIAL_STATE: LessonSliceState = {
  isLoading: false,
  isGodMode: false,
  currentLesson: undefined,
  currentTask: undefined,
  currentStep: undefined,
  currentLessonTaskStepKey: LessonTaskStepKeys.DEFAULT_CURRENT,
  taskStepViewCount: {},
};

const persistKeys: (keyof LessonSliceState)[] = [
  'currentLesson',
  'currentTask',
  'currentStep',
  'currentLessonTaskStepKey',
  'taskStepViewCount',
];

const persistConfig = {
  key: LESSON_SLICE_NAME,
  storage: getReduxStorageUtil(),
  whitelist: persistKeys,
} as PersistConfig<LessonSliceState>;

const lessonSlice = createSlice({
  name: LESSON_SLICE_NAME,
  initialState: LESSON_SLICE_INITIAL_STATE,
  reducers: {
    resetLessonState: () => {
      return LESSON_SLICE_INITIAL_STATE;
    },
    setIsLoading: (state, action: PayloadAction<boolean>) => {
      state.isLoading = action.payload;
    },
    toggleGodMode: (state) => {
      state.isGodMode = !state.isGodMode;
    },
    setCurrentLessonTaskStepKey: (state, action: PayloadAction<LessonTaskStepKey>) => {
      state.currentLessonTaskStepKey = action.payload;
      const key = action.payload;
      updateLessonTaskStepFromKey(state, key);
    },
    moveToNextTask: (state) => {
      const { currentLessonTaskStepKey } = state;
      if (!currentLessonTaskStepKey) return;

      const nextTaskKey = lessonUtil.getNextLessonTaskKey(currentLessonTaskStepKey);
      state.currentLessonTaskStepKey = nextTaskKey;
      updateLessonTaskStepFromKey(state, nextTaskKey);
    },
    moveToNextStep: (state) => {
      const { currentLessonTaskStepKey } = state;
      if (!currentLessonTaskStepKey) return;

      const nextStepKey = lessonUtil.getNextStepKey(currentLessonTaskStepKey);
      state.currentLessonTaskStepKey = nextStepKey;
      updateLessonTaskStepFromKey(state, nextStepKey);
    },
    moveToPreviousTask: (state) => {
      const { currentLessonTaskStepKey } = state;
      if (!currentLessonTaskStepKey) return;

      const previousTaskKey = lessonUtil.getPreviousLessonTaskKey(currentLessonTaskStepKey);
      state.currentLessonTaskStepKey = previousTaskKey;
      updateLessonTaskStepFromKey(state, previousTaskKey);
    },
    moveToPreviousStep: (state) => {
      const { currentLessonTaskStepKey } = state;
      if (!currentLessonTaskStepKey) return;

      const previousStepKey = lessonUtil.getPreviousStepKey(currentLessonTaskStepKey);
      state.currentLessonTaskStepKey = previousStepKey;
      updateLessonTaskStepFromKey(state, previousStepKey);
    },
    incrementTaskStepViewCount: (state) => {
      const { currentTask, currentStep } = state;
      if (!currentTask || !currentStep) return;
      if (currentStep.scriptKey) return;

      if (!state.taskStepViewCount[currentTask.taskType]) {
        state.taskStepViewCount[currentTask.taskType] = {};
      }
      if (!state.taskStepViewCount[currentTask.taskType]![currentStep.stepType]) {
        state.taskStepViewCount[currentTask.taskType]![currentStep.stepType] = 0;
      }
      state.taskStepViewCount[currentTask.taskType]![currentStep.stepType]!++;
    },
  },
  extraReducers: (builder) => {
    openLessonThunkExtraReducers(builder);
  },
});

export const {
  resetLessonState,
  setIsLoading,
  setCurrentLessonTaskStepKey,
  toggleGodMode,
  moveToNextTask,
  moveToNextStep,
  moveToPreviousTask,
  moveToPreviousStep,
  incrementTaskStepViewCount,
} = lessonSlice.actions;

export const LessonSlice = persistReducer(persistConfig, lessonSlice.reducer);
